import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Box, Checkbox, Popover, Typography } from '@mui/material'
import React, { useState } from 'react'

import Button from '@/components/Button'
import type { ImageLabel } from '@/utils/constants'

interface FilterByLabelMenuProps {
  allLabels: ImageLabel[]
  activeFilterIds: number[]
  onChange: (labelIds: number[]) => void
  isLoading?: boolean
}

export default function FilterByLabelMenu({
  allLabels,
  activeFilterIds,
  onChange,
  isLoading = false,
}: FilterByLabelMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const allSelected =
    allLabels.length > 0 && activeFilterIds.length === allLabels.length

  const handleToggleLabel = (labelId: number) => {
    if (activeFilterIds.includes(labelId)) {
      onChange(activeFilterIds.filter((id) => id !== labelId))
    } else {
      onChange([...activeFilterIds, labelId])
    }
  }

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([])
    } else {
      onChange(allLabels.map((l) => Number(l.id)))
    }
  }

  const buttonLabel =
    activeFilterIds.length > 0
      ? `Filter By Label (${activeFilterIds.length})`
      : 'Filter By Label'

  return (
    <>
      <Box
        id="filter-by-label-btn"
        onClick={(e) => !isLoading && setAnchorEl(e.currentTarget)}
        display="inline-flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          height: '36px',
          px: '14px',
          border: '1px solid rgba(255,255,255,0.23)',
          borderRadius: '4px',
          cursor: isLoading ? 'default' : 'pointer',
          color:
            activeFilterIds.length > 0 ? '#90caf9' : 'rgba(255,255,255,0.87)',
          fontSize: '14px',
          fontWeight: 400,
          letterSpacing: '0.17px',
          userSelect: 'none',
          opacity: isLoading ? 0.5 : 1,
          minWidth: '120px',
          transition: 'border-color 0.15s',
          '&:hover': {
            borderColor: open
              ? 'rgba(255,255,255,0.23)'
              : 'rgba(255,255,255,0.87)',
          },
        }}
      >
        {buttonLabel}
        <ArrowDropDownIcon
          sx={{
            fontSize: 22,
            color: 'rgba(255,255,255,0.54)',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            mr: -0.5,
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#2a2a2a',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '4px',
              minWidth: '220px',
              mt: 0.5,
              boxShadow: '0px 8px 24px rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        {/* ── SELECT ALL / DESELECT ALL ── */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Button
            id="filter-select-all-btn"
            label={allSelected ? 'DESELECT ALL' : 'SELECT ALL'}
            variant="outlined"
            color="primary"
            size="small"
            textTransform="uppercase"
            onClick={handleSelectAll}
            sx={{ fontSize: '11px', width: '100%' }}
          />
        </Box>

        {/* ── Label list ── */}
        {allLabels.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography sx={{ color: '#888', fontSize: '13px' }}>
              No labels available
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: '320px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: '#ffb572',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-track': { bgcolor: '#1a1a1a' },
            }}
          >
            {allLabels.map((label) => {
              const checked = activeFilterIds.includes(Number(label.id))
              return (
                <FilterLabelRow
                  key={label.id}
                  label={label}
                  checked={checked}
                  onToggle={() => handleToggleLabel(Number(label.id))}
                />
              )
            })}
          </Box>
        )}
      </Popover>
    </>
  )
}

// ── Filter label row ─────────────────────────────────────────────────────────

interface FilterLabelRowProps {
  label: ImageLabel
  checked: boolean
  onToggle: () => void
}

function FilterLabelRow({ label, checked, onToggle }: FilterLabelRowProps) {
  const [hovered, setHovered] = useState(false)

  return (
    <Box
      display="flex"
      alignItems="center"
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        bgcolor: checked
          ? 'rgba(255, 255, 255, 0.08)'
          : hovered
            ? 'rgba(255, 255, 255, 0.04)'
            : 'transparent',
        transition: 'background-color 0.12s ease',
      }}
    >
      <Checkbox
        checked={checked}
        size="small"
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
        sx={{
          p: 0,
          mr: 1,
          color: '#666',
          '&.Mui-checked': { color: '#90caf9' },
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: '13px',
          color: 'white',
          fontWeight: checked ? 500 : 400,
          lineHeight: 1.4,
        }}
      >
        {label.name}
      </Typography>
    </Box>
  )
}
